'use client'

import { HTMLAttributes } from 'react'
import type {
  IconDefinition,
  IconProp,
} from '@fortawesome/fontawesome-svg-core'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { mtc } from '../../../utils/style-helpers'
import { Icon } from '../../atoms/icon'
import { Badge, type BadgeColor } from '../../atoms/badge'
import {
  timelineStyles,
  stepCircleStyles,
  connectorStyles,
  stepLabelStyles,
} from './styles'

export enum TimelineStepStatus {
  NotStarted = 'not_started',
  InProgress = 'in_progress',
  Review = 'review',
  Complete = 'complete',
}

export type TimelineStepBadge = {
  label: string
  color: BadgeColor
}

export type TimelineStepOverride = {
  label?: string
  icon?: IconDefinition | IconProp
  badge?: TimelineStepBadge | null
}

export type TimelineStep = {
  label: string
  icon?: IconDefinition | IconProp
  iconClassName?: string
  /** Overrides the status derived from activeStep */
  status?: TimelineStepStatus
  /** Per-state label and icon overrides */
  states?: Partial<Record<TimelineStepStatus, TimelineStepOverride>>
  badge?: TimelineStepBadge
}

export interface VisualTimelineProps extends HTMLAttributes<HTMLDivElement> {
  steps: TimelineStep[]
  /**
   * 1-based index of the current in-progress step.
   * 0 = nothing started, 1 = first step in progress, etc.
   */
  activeStep?: number
}

export const VisualTimeline = ({
  steps,
  activeStep = 0,
  className,
  ...props
}: VisualTimelineProps) => {
  if (process.env.NODE_ENV !== 'production' && steps.length < 3) {
    console.warn('VisualTimeline: steps should have at least 3 items')
  }

  const getStatus = (index: number, step: TimelineStep): TimelineStepStatus => {
    if (step.status) return step.status
    if (index < activeStep - 1) return TimelineStepStatus.Complete
    if (index === activeStep - 1) return TimelineStepStatus.InProgress
    return TimelineStepStatus.NotStarted
  }

  return (
    <div className={mtc(timelineStyles(), className)} {...props}>
      {steps.map((step, index) => {
        const status = getStatus(index, step)
        const isLast = index === steps.length - 1
        const override = step.states?.[status]
        const displayLabel = override?.label ?? step.label
        const displayIcon = override?.icon ?? step.icon
        const displayBadge =
          override?.badge !== undefined ? override.badge : step.badge

        return (
          <div key={step.label} className="flex gap-4 items-start">
            <div className="flex flex-col items-center self-stretch">
              <div className={stepCircleStyles({ status })}>
                {status === TimelineStepStatus.InProgress && displayIcon && (
                  <Icon
                    icon={displayIcon}
                    color="text-green-100"
                    className={step.iconClassName}
                  />
                )}
                {status === TimelineStepStatus.Review && displayIcon && (
                  <Icon icon={displayIcon} color="text-green-100" />
                )}
                {status === TimelineStepStatus.Complete && (
                  <Icon icon={faCheck} color="text-neutral-0" />
                )}
              </div>
              {!isLast && <div className={connectorStyles()} />}
            </div>

            <div
              className={mtc(
                stepLabelStyles({ status }),
                'flex items-center gap-2',
                !isLast && 'pb-8'
              )}
            >
              <span>{displayLabel}</span>
              {displayBadge && (
                <Badge color={displayBadge.color} variant="secondary" size="sm">
                  {displayBadge.label}
                </Badge>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

VisualTimeline.displayName = 'VisualTimeline'
